import { Box, Grid, IconButton, Select, MenuItem, FormControl } from "@mui/material"
import { Card, Chart, ChartIntervals, Chip, Icon, Spinner, Tooltip, Typography, designTokens } from "@platform-ui/design-system"
import React, { Dispatch, useCallback, useEffect, useMemo, useState } from "react"
import { ExistingDestination, Transfer, useListTransfers } from "@prequel/react";
import { DataItem } from "@platform-ui/design-system/dist/types/Chart";
import { Action, useStoreContext } from "../../../../Store";
import { IADState } from "../../../../IntegrationApps/IntegrationAppDetails/state";
import fetchAuthToken from "../fetchTokens/fetchAuthToken";
import { AUTH_TOKEN_URL, LAST_SYNC_STATUS_MAPPINGS, PREQUEL_API_HOST, T9_PERMISSION_MAPPINGS, TimezoneConfig, TimezoneKey, TransferStatus } from "../Constants";
import Connect from "../../../../Connect/Connect";
import MetricsInfoCard from "./MetricsInfoCard";
import { useIdentity } from "../../../../IdentityProvider";
import { IdentityPermissions } from "../../../../IdentityProvider/constants/permissions";
import { ZDQ_DESTINATION_DATA, ZDQ_RECIPIENT } from "../action_types";
import _ from 'lodash';
import { StatusPopoverComp } from "../../StatusPopoverComp";
import { TransfersViewDialog } from "./TransfersViewDialog";
import { formatDateTime, TIMEZONE_CONFIG } from "../utils";

declare type TransferData = {
    day: string,
    total_rows_transferred: number,
    total_transfers_completed: number,
    total_volume_transferred_in_mb: number
}

const getTimezoneLabel = (timezone: TimezoneKey): string => {
    if (timezone === TimezoneKey.UTC) return 'UTC Time';
    else if (timezone === TimezoneKey.PDT) return 'Pacific Time (PST/PDT)';

    const now = new Date();
    const formatted = new Intl.DateTimeFormat('en-US', {
        timeZone: TIMEZONE_CONFIG[timezone].getIdentifier(),
        timeZoneName: 'short'
    }).format(now);
    const abbr = formatted.split(' ').pop() || 'local';
    return `Local Time (${abbr})`;
};

enum ChartViewMode {
    DAILY_COUNT = 'dailyCount',
    TOTAL_VOLUME = 'totalVolume'
}

const CHART_VIEW_MODE_LABELS: Record<ChartViewMode, string> = {
    [ChartViewMode.DAILY_COUNT]: 'Daily',
    [ChartViewMode.TOTAL_VOLUME]: 'Accumulated'
};

function ZdqConnectorStatus({ vendor }) {

    // States
    const [originalTransferData, setOriginalTransferData] = useState<TransferData[]>([]);
    const [totalRowsSynced, setTotalRowsSynced] = useState<string>('0');
    const [showSpinner, setShowSpinner] = useState(false);
    const [lastTransfer, setLastTransfer] = useState(null);
    const [shareLatency, setShareLatency] = useState<string>(null);
    const [isLastSyncStatusPopoverClosed, setIsLastSyncStatusPopoverClosed] = useState(false);
    const [selectedTimezone, setSelectedTimezone] = useState<TimezoneKey>(TimezoneKey.LOCAL);

    // States for transfer list dialog
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedTransfersDate, setSelectedTransfersDate] = useState<string | null>(null);
    const [transfersListPerDate, setTransfersListPerDate] = useState<Transfer[]>([]);
    const [loadingTransfersView, setLoadingTransfersView] = useState(false);

    // States for chart view mode
    const [chartData, setChartData] = useState<DataItem[]>([]);
    const [cumulativeChartData, setCumulativeChartData] = useState<DataItem[]>([]);
    const [chartViewMode, setChartViewMode] = useState<ChartViewMode>(ChartViewMode.DAILY_COUNT);

    const isLocal = window.location.host.includes('localhost');
    const proxyPath = `${isLocal ? 'http://localhost:8080' : ''}/platform/gateway-proxy-v2`;

    // Context
    const { state } = useStoreContext() as { state: IADState, dispatch: Dispatch<Action> };
    const destinationData = state.settingsHash[ZDQ_DESTINATION_DATA];
    const recipient = state.settingsHash[ZDQ_RECIPIENT];
    const fetchAuthTokenUrl = `${state.settingsUrl.replace('.json', AUTH_TOKEN_URL)}`;

    // T9 Permissions
    const { hasPermission } = useIdentity();
    const hasPermissionsEnabled = hasPermission(`permission.${T9_PERMISSION_MAPPINGS[vendor]}` as IdentityPermissions);

    const getNextTransferTime = (() => {
        if (shareLatency && (lastTransfer?.started_at || lastTransfer?.submitted_at)) {
            const lastTransferAttempt = new Date(lastTransfer.started_at || lastTransfer.submitted_at);
            const lastTransferDifferenceAgainstCurrentTimeInSeconds = (new Date().getTime() - lastTransferAttempt?.getTime()) / 1000;
            const shareLatencyInSeconds = parseInt(shareLatency);
            const nextTransferTime: number = Math.abs(shareLatencyInSeconds - lastTransferDifferenceAgainstCurrentTimeInSeconds) / (60 * 60)

            if (_.isNaN(shareLatencyInSeconds) || _.isNaN(nextTransferTime)) {
                Connect.log(`Next transfer time error:
                    shareLatencyInMinutes = ${shareLatencyInSeconds}, 
                    nextTransferTime ${nextTransferTime} 
                    lastTransferAttempt = ${lastTransferAttempt}`);
                return 'In about unknown hours'
            }

            /* Ideally, there should be never be a case when the shareLatencyInSeconds > nextTransferTime because the transfer take place every 'shareLatencyInSeconds' seconds.
               But, megastore works in a way such that it might take more that 'shareLatencyInSeconds' time for the next transfer.
               Therefore, returning the 'shareLatencyInSeconds' value directly until a transfer takes place.
            */
            if (shareLatencyInSeconds < lastTransferDifferenceAgainstCurrentTimeInSeconds) {
                return `In about ${Math.ceil(shareLatencyInSeconds < 3600 ? shareLatencyInSeconds / 60 : shareLatencyInSeconds / 3600)} ${shareLatencyInSeconds < 3600 ? ' minutes' : ' hours'}`;
            }
            return `In about ${Math.ceil(nextTransferTime < 1 ? nextTransferTime * 60 : nextTransferTime)} ${nextTransferTime < 1 ? ' minutes' : ' hours'}`;
        }
        return 'In about unknown hours'
    });

    const gridItems = useMemo(
        () => [
            { id: '1', title: 'Last successful Transfer Completed', tooltip: `This timestamp marks the completion of the most recent successful data transfer to your destination. It reflects the latest data available in your system. The timestamp is displayed in ${getTimezoneLabel(selectedTimezone)}.`, data: destinationData?.last_successful_transfer_ended_at || '' },
            { id: '2', title: 'Next Scheduled Transfer Starts', tooltip: 'This timestamp indicates when the next synchronization process will commence', data: getNextTransferTime() },
            { id: '3', title: `Total Rows Synced ${chartViewMode === ChartViewMode.DAILY_COUNT ? ' (last 30 days)' : ' (All Time)'}`, tooltip: 'This indicates the total number of rows synchronized during the last 30 days. It provides insight into the recent data transfer activity for the connector.', data: '' }
        ],
        [shareLatency, lastTransfer, selectedTimezone]
    );

    // Error status popover logic
    const isDisplayErrorPopover = lastTransfer && (lastTransfer?.status == TransferStatus.ERROR || lastTransfer?.status == TransferStatus.PARTIAL_FAILURE);
    const formattedDate = formatDateTime(lastTransfer?.started_at || lastTransfer?.submitted_at, selectedTimezone);
    const statusPopoverBody = () => {
        if (lastTransfer && lastTransfer?.status === TransferStatus.ERROR) {
            return (
                <Typography variant="body2" sx={{ fontWeight: 400, opacity: 0.6 }}>
                    The last transfer, submitted at {formattedDate}, did not complete successfully. We are actively investigating the issue to ensure all your data is fully synced. Please check back later or contact support for more information.
                </Typography>
            );
        } else if (lastTransfer && lastTransfer?.status === TransferStatus.PARTIAL_FAILURE) {
            const impactedObjects = lastTransfer?.model_metrics
                ?.filter(metric => metric.error !== null)
                ?.map(metric => metric.model_name)
                ?.sort() || [];

            return (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 400, opacity: 0.6, marginBottom: 1 }}>
                        Some objects failed to sync. We're actively working to resolve the issue.
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 400, opacity: 0.6, marginBottom: 2 }}>
                        If failures continue or impact critical data, please contact support.
                    </Typography>
                    {impactedObjects.length > 0 && (
                        <>
                            <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.8, marginBottom: 1 }}>
                                Impacted Objects:
                            </Typography>
                            <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                                {impactedObjects.map((objName, index) => (
                                    <Typography component="li" key={index} variant="body2" sx={{ fontWeight: 400, opacity: 0.6, listStyleType: 'disc' }}>
                                        {objName}
                                    </Typography>
                                ))}
                            </Box>
                        </>
                    )}
                </Box>
            );
        }
        return <></>;
    }

    // Transfer Hook
    const fetchAuthTokenWrapper = useCallback(async () => {
        return await fetchAuthToken(fetchAuthTokenUrl);
    }, [fetchAuthTokenUrl]);
    const listTransfers = useListTransfers(fetchAuthTokenWrapper, window.location.host, PREQUEL_API_HOST);

    // Fetch transfers for a specific date using our custom endpoint
    const fetchTransfersListPerDate = async (dateString: string) => {
        try {
            const csrf = document.querySelector('meta[name=\'csrf-token\']').getAttribute('content');
            const options = {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-Token': csrf
                })
            };

            // Prequel API range is [ended_after, ended_before) — inclusive start, exclusive end.
            // For a PDT calendar day (UTC-7), use 07:00 UTC on that date through 07:00 UTC on the next date.
            const startOfDay = new Date(dateString);
            startOfDay.setUTCHours(7, 0, 0, 0);
            const ended_after = startOfDay.toISOString();

            const endOfDay = new Date(dateString);
            endOfDay.setUTCDate(endOfDay.getUTCDate() + 1);
            endOfDay.setUTCHours(7, 0, 0, 0);
            const ended_before = endOfDay.toISOString();

            const url = `${state.settingsUrl.replace('.json', `/zdq/single_day_transfers?destination_id=${destinationData?.id}&ended_after=${ended_after}&ended_before=${ended_before}`)}`;
            const response = await window.fetch(url, options);
            const data = await response.json();
            if (!response.ok) {
                throw Error(`Error fetching transfers for date: ${dateString}, message: ${data.message}, statusText: ${response.statusText}`);
            }
            return data;
        } catch (error) {
            Connect.log(error);
            return [];
        }
    };

    // Handle chart bar click
    const handleChartBarClick = async (dataItem: DataItem) => {
        if (!dataItem || !dataItem.x) return;
        try {
            setLoadingTransfersView(true);
            setSelectedTransfersDate(dataItem.x as string);
            setIsDialogOpen(true);
            const listOfTransfers = await fetchTransfersListPerDate(dataItem.x as string);
            setTransfersListPerDate(listOfTransfers || []);
        } catch (error) {
            Connect.log('Error fetching transfers:', error);
            setTransfersListPerDate([]);
        } finally {
            setLoadingTransfersView(false);
        }
    };

    // All transfers to display the chart
    async function fetchAllTransfers(startDate: string, endDate: string) {
        try {
            const csrf = document.querySelector('meta[name=\'csrf-token\']').getAttribute('content');
            const options = {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-Token': csrf
                })
            };
            const response = await window.fetch(`${state.settingsUrl.replace('.json', `/zdq/transfers?destination_id=${destinationData?.id}&start_date=${startDate}&end_date=${endDate}`)}`, options);
            const data = await response.json();
            if (!response.ok) {
                throw Error(`Error fetching All transfers for the destination: ${destinationData?.id}, message: ${data.message}, statusText: ${response.statusText}`);
            }
            return data;

        } catch (error) {
            Connect.log(error);
        }
    }

    // Get the latency for the tenant from tenant-registry
    const fetchTenantLatency = async () => {
        try {
            const response = await Connect.proxyCall(
                `${proxyPath}/tenant-registry/tenantsServiceInfo/${recipient?.id_in_provider_system}?name=ZWAREHOUSECONNECTOR&type=WAREHOUSE`,
                'GET',
                undefined,
                { 'Accept': 'application/json', 'Content-Type': 'application/json' }
            );
            if (!response.ok) {
                const body = await response.json()
                throw new Error(`Error fetchTenantLatency: ${body?.message}, statusText: ${response.statusText}, tenant: ${recipient?.id_in_provider_system}`);
            }
            const res = await response.json();
            if (res.length !== 1) {
                throw new Error("None or More than one element found in the tenants service info");
            }
            return res[0]?.info?.shareLatencyInSeconds
        } catch (error) {
            Connect.log(`Error fetching latency for the ${recipient?.id_in_provider_system} tenant: ${error}`);
        }
    };

    // Fetch chart data based on last transfer and chart view mode
    const fetchTransfersData = useCallback(async (transferData: Transfer) => {
        try {
            setShowSpinner(true);
            const endDateTime = transferData?.ended_at;
            const startDateTime = chartViewMode === ChartViewMode.DAILY_COUNT ?
                (() => {
                    const date = new Date(endDateTime);
                    date.setUTCDate(date.getUTCDate() - 30);
                    const originalFraction = endDateTime?.split(".")[1]?.replace("Z", "") || "";
                    const iso = date.toISOString();
                    if (originalFraction) {
                        const [base] = iso.split(".");
                        return `${base}.${originalFraction}Z`;
                    }
                    return iso;
                })() :
                recipient?.created_at;

            // Update the start and end dates to the previous and next day respectively in PDT timezone
            const startDate = (() => {
                const date = new Date(startDateTime);
                date.setUTCDate(date.getUTCDate() - 1);
                date.setUTCHours(7, 0, 0, 0);
                const originalFraction = startDateTime?.split(".")[1]?.replace("Z", "") || "";
                const iso = date.toISOString();
                if (originalFraction) {
                    const [base] = iso.split(".");
                    return `${base}.${originalFraction}Z`;
                }
                return iso;
            })();

            const endDate = (() => {
                const date = new Date(endDateTime);
                date.setUTCDate(date.getUTCDate() + 1);
                date.setUTCHours(7, 0, 0, 0);
                const originalFraction = endDateTime?.split(".")[1]?.replace("Z", "") || "";
                const iso = date.toISOString();
                if (originalFraction) {
                    const [base] = iso.split(".");
                    return `${base}.${originalFraction}Z`;
                }
                return iso;
            })();

            const allTransfers = await fetchAllTransfers(startDate, endDate);
            if (!allTransfers || allTransfers?.error) {
                Connect.log(`Error while fetching transfers: ${allTransfers?.error}`);
                return;
            }
            // Process chart data with minimum visible height for small values using minVisibleThreshold
            const sortedData = allTransfers?.sort((a: TransferData, b: TransferData) => new Date(a.day).getTime() - new Date(b.day).getTime()) || [];
            if (sortedData.length === 0) {
                setChartData([]);
                setCumulativeChartData([]);
                setOriginalTransferData([]);
                return;
            }
            setOriginalTransferData(sortedData);

            // Calculate the no of rows synced for the last 30 days.
            const maxValue = Math.max(...sortedData.map(d => d.total_rows_transferred));
            const minVisibleThreshold = maxValue > 0 ? maxValue * 0.02 : 1; // 2% of max value for minimum visibility
            const processedChartData: DataItem[] = sortedData.map((data: TransferData) => {
                const actualValue = data.total_rows_transferred;
                const displayValue = actualValue > 0 && actualValue < minVisibleThreshold
                    ? minVisibleThreshold
                    : actualValue;
                return {
                    x: new Date(data.day).toISOString(),
                    y: displayValue
                };
            });
            setChartData(processedChartData);
            const dailyCountRowsSynced = allTransfers.reduce(
                (accumulator, currentValue) => accumulator + currentValue.total_rows_transferred,
                0
            );
            setTotalRowsSynced(dailyCountRowsSynced.toLocaleString());

            // Calculate the cumulative no of rows synced for all time.
            const cumulativeResult: { data: DataItem[]; runningTotal: number } = sortedData.reduce(
                (acc, currentData) => {
                    const newTotal = acc.runningTotal + currentData.total_rows_transferred;
                    return {
                        data: [
                            ...acc.data,
                            {
                                x: new Date(currentData.day).toISOString(),
                                y: newTotal
                            }
                        ],
                        runningTotal: newTotal
                    };
                },
                { data: [] as DataItem[], runningTotal: 0 }
            );
            setCumulativeChartData(cumulativeResult.data);
        } catch (error) {
            Connect.log('Zdq all transfer: ', error);
        } finally {
            setShowSpinner(false);
        }
    }, [chartViewMode, recipient, destinationData]);

    // Fetch last transfer, tenant latency and chart data
    useEffect(() => {
        const fetchData = async () => {
            setShowSpinner(true);
            try {
                const currentTransfer = lastTransfer || await (async () => {
                    const lastTransferResponse = await listTransfers(destinationData, { pageSize: 1 });
                    if (!lastTransferResponse || !lastTransferResponse.results) {
                        throw Error("Error while fetching last transfer");
                    }
                    if (lastTransferResponse.results?.length === 1) {
                        setLastTransfer(lastTransferResponse.results[0]);
                        const latency = await fetchTenantLatency();
                        setShareLatency(latency);
                        Connect.log('Zdq last transfer response: ', lastTransferResponse.results.length);
                        return lastTransferResponse.results[0];
                    } else {
                        setLastTransfer(null);
                        Connect.log('Zdq last transfer empty response: ', lastTransferResponse.results?.length || 0);
                        return null;
                    }
                })();
                if (currentTransfer) {
                    await fetchTransfersData(currentTransfer);
                }
            } catch (error) {
                Connect.log('Zdq data fetch failed: ', error);
            } finally {
                setShowSpinner(false);
            }
        };

        if (hasPermissionsEnabled && recipient && !_.isEmpty(destinationData)) {
            fetchData();
        }
    }, [recipient, destinationData, chartViewMode])

    const onCloseTransfersDialog = () => {
        setIsDialogOpen(false);
        setSelectedTransfersDate(null);
        setTransfersListPerDate([]);
    };

    if (!hasPermissionsEnabled) {
        return <Card autoDistance id="disabled-tenant-card" body={
            <Typography color={designTokens.colors.coolGray300}
                sx={{ marginTop: '5%', marginBottom: '5%', textAlign: 'center' }}
                variant="body1"
                body="You don't have permissions enabled to view this tab. Please contact Zuora support if you are seeing this message." />}
        />
    }

    if (showSpinner || destinationData === undefined) {
        return <Spinner e2e="connector-status-spinner" size="large"></Spinner>
    }

    if (!recipient) {
        return <Card autoDistance id="connector-setup-no-recipient-or-models" body={
            <Typography color={designTokens.colors.coolGray300}
                sx={{ marginTop: '5%', marginBottom: '5%', textAlign: 'center' }}
                variant="body1"
                body="Error while fetching the recipient details for this tenant. Please contact Zuora support if you are seeing this message." />
        }
        />
    }

    if (!_.isEmpty(destinationData)) {
        return (
            <>
                <TransfersViewDialog
                    isOpen={isDialogOpen}
                    onClose={onCloseTransfersDialog}
                    selectedTransferDate={selectedTransfersDate}
                    transfersList={transfersListPerDate}
                    loading={loadingTransfersView}
                    selectedTimezone={TimezoneKey.PDT} // Update the display to use the user-selected timezone once the Prequel API supports multiple timezones
                    destinationData={destinationData}
                    settingsUrl={state.settingsUrl}
                    lastTransfer={lastTransfer}
                />
                <Card autoDistance id='connector-status-metrics'
                    header={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Typography variant='title' body="Connector Metrics" />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontSize: '1rem' }} body="Timezone:" />
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <Select
                                        value={selectedTimezone}
                                        onChange={(e) => setSelectedTimezone(e.target.value as TimezoneKey)}
                                        sx={{
                                            fontSize: '1rem',
                                            height: '32px',
                                            '& .MuiSelect-select': {
                                                paddingTop: '4px',
                                                paddingBottom: '4px',
                                            }
                                        }}
                                    >
                                        {Object.keys(TIMEZONE_CONFIG).map((key: TimezoneKey) => (
                                            <MenuItem
                                                value={key}
                                                key={`timezone-option-${key}`}
                                                sx={{ fontSize: '1rem' }}
                                            >
                                                {getTimezoneLabel(key)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    } titleBar
                    noPaddingBottom
                    body={
                        <Grid container direction='column'>
                            <Grid item>
                                <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                                    <Typography sx={{ marginLeft: '8px', marginRight: '8px' }} variant="body1"
                                        color={designTokens.colors.coolGray500} body="Sync Transfer Status:" />
                                    <StatusPopoverComp
                                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                                        onFirstOpen={() => {
                                            setIsLastSyncStatusPopoverClosed(false);
                                        }}
                                        anchor={
                                            <Chip label={LAST_SYNC_STATUS_MAPPINGS[lastTransfer?.status]?.label}
                                                state={LAST_SYNC_STATUS_MAPPINGS[lastTransfer?.status]?.state}
                                                icon={LAST_SYNC_STATUS_MAPPINGS[lastTransfer?.status]?.icon} />
                                        }
                                        body={
                                            <Grid
                                                container
                                                sx={{
                                                    width: "300px",
                                                    padding: "12px 18px",
                                                }}
                                                spacing={1}
                                                direction="column"
                                            >
                                                <Grid item container justifyContent="space-between" alignItems="center">
                                                    <Grid item>
                                                        <Typography variant="title" sx={{ fontWeight: 600 }} body={LAST_SYNC_STATUS_MAPPINGS[lastTransfer?.status]?.label} />
                                                    </Grid>
                                                    <Grid item>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setIsLastSyncStatusPopoverClosed(true)}
                                                        >
                                                            <Icon body="close" variant="outlined" />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                                <Grid item>
                                                    {statusPopoverBody()}
                                                </Grid>
                                            </Grid>
                                        }
                                        displayPopover={isDisplayErrorPopover && !isLastSyncStatusPopoverClosed}
                                        popoverId="connection-status-popover"
                                    />
                                </Box>
                            </Grid>
                            <Grid container direction='row'>
                                <Grid item xs={4}>
                                    <MetricsInfoCard data={gridItems[0].data ? formatDateTime(gridItems[0].data, selectedTimezone) : 'No Transfer Completed Yet'} title={gridItems[0].title} tooltip={gridItems[0].tooltip} />
                                </Grid>
                                <Grid item xs={4}>
                                    <MetricsInfoCard data={gridItems[1].data} title={gridItems[1].title} tooltip={gridItems[1].tooltip} ></MetricsInfoCard>
                                </Grid>
                                <Grid item xs={4}>
                                    <MetricsInfoCard data={totalRowsSynced} title={gridItems[2].title} tooltip={gridItems[2].tooltip} ></MetricsInfoCard>
                                </Grid>
                            </Grid>
                        </Grid>
                    }>
                </Card >
                <Card autoDistance
                    id='connector-status-rows-synced'
                    header={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant='title' body={chartViewMode === ChartViewMode.DAILY_COUNT ? 'Rows Synced' : 'Cumulative Rows Synced'} sx={{ marginRight: '8px' }} />
                                <Icon
                                    body="info"
                                    tooltip={
                                        <Typography variant="body1" body={chartViewMode === ChartViewMode.DAILY_COUNT
                                            ? "This chart displays the number of rows synced on each day as per the Pacific Time (PST/PDT) only."
                                            : "This chart displays the cumulative total rows synced up to each day as per the Pacific Time (PST/PDT)."
                                        } />
                                    }
                                />
                            </Box>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <Select
                                    value={chartViewMode}
                                    onChange={(e) => setChartViewMode(e.target.value as ChartViewMode)}
                                    sx={{
                                        fontSize: '1rem',
                                        height: '32px',
                                        '& .MuiSelect-select': {
                                            paddingTop: '4px',
                                            paddingBottom: '4px',
                                        }
                                    }}
                                >
                                    {Object.values(ChartViewMode).map((mode: ChartViewMode) => (
                                        <MenuItem
                                            value={mode}
                                            key={`chart-view-mode-${mode}`}
                                            sx={{ fontSize: '1rem' }}
                                        >
                                            {CHART_VIEW_MODE_LABELS[mode]}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    }
                    titleBar
                    noPaddingBottom
                    body={
                        <Box sx={{ '> div': { fontSize: '1.5rem' } }}>
                            <Typography color={designTokens.colors.coolGray400} body={chartViewMode === ChartViewMode.DAILY_COUNT ? 'Last 30 days (Pacific Time (PST/PDT))' : 'Total volume over time (Pacific Time (PST/PDT))'} />
                            <Chart
                                colorPalette="single"
                                type="bar"
                                data={chartViewMode === ChartViewMode.DAILY_COUNT ? chartData : cumulativeChartData}
                                xInterval={ChartIntervals.DAY}
                                yAxisIntegerOnly
                                showAllXAxisLabel
                                dsOnChartElClick={(dataItem) => {
                                    chartViewMode === ChartViewMode.DAILY_COUNT && handleChartBarClick(dataItem);
                                }}
                                tooltip={(props) => {
                                    const currentData = chartViewMode === ChartViewMode.DAILY_COUNT ? chartData : cumulativeChartData;
                                    return (
                                        <Grid container sx={{ margin: '10' }}>
                                            <Grid item>
                                                <Typography variant="title" body={currentData[props.currIndex]?.x ? new Intl.DateTimeFormat('en-US', {
                                                    month: 'long',
                                                    day: 'numeric',
                                                    year: 'numeric'
                                                }).format(new Date(currentData[props.currIndex].x)) : 'Unknown'} />
                                            </Grid>
                                            <Grid container direction="row" justifyContent="space-between" sx={{ marginTop: '10%' }}>
                                                <Grid item>
                                                    <Typography variant="subtitle1">
                                                        {chartViewMode === ChartViewMode.DAILY_COUNT ? 'Rows Synced' : 'Cumulative Total'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Typography variant="subtitle1" body={
                                                        chartViewMode === ChartViewMode.DAILY_COUNT
                                                            ? (originalTransferData[props.currIndex]?.total_rows_transferred?.toLocaleString() || currentData[props.currIndex]?.y?.toLocaleString() || '0')
                                                            : (currentData[props.currIndex]?.y?.toLocaleString() || '0')
                                                    } />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    );
                                }}
                            />
                        </Box>
                    }
                >
                </Card>
            </>);
    }
    return <Card autoDistance id="connector-status-no-destination" body={
        <Typography color={designTokens.colors.coolGray300} sx={{ marginTop: '5%', marginBottom: '5%', textAlign: 'center' }} variant="body1" body="No destination has been configured for this tenant. Please add a destination to see its status. If you have added a destination and still see this message, please contact zuora support."></Typography>} />
}

export default ZdqConnectorStatus